import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ReviewForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    product_name: '',
    description: '',
    rating: 5,
    pros: '',
    cons: '',
    specs: '',
    affiliate_link: '',
    featured_image: '',
    author: 'Editor',
    category_id: '',
    tag_ids: [],
    published: false
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [categoriesRes, tagsRes] = await Promise.all([
        axios.get(`${API_URL}/api/categories`),
        axios.get(`${API_URL}/api/tags`)
      ]);

      setCategories(categoriesRes.data);
      setTags(tagsRes.data);

      if (isEdit) {
        const { data } = await axios.get(`${API_URL}/api/admin/reviews/${id}`, {
          withCredentials: true
        });
        setFormData({
          ...data,
          category_id: data.category.id,
          tag_ids: data.tags.map(t => t.id)
        });
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      const payload = {
        ...formData,
        rating: parseFloat(formData.rating),
        category_id: parseInt(formData.category_id)
      };

      if (isEdit) {
        await axios.put(`${API_URL}/api/admin/reviews/${id}`, payload, {
          withCredentials: true
        });
      } else {
        await axios.post(`${API_URL}/api/admin/reviews`, payload, {
          withCredentials: true
        });
      }

      navigate('/admin/reviews');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save review');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleTag = (tagId) => {
    setFormData(prev => ({
      ...prev,
      tag_ids: prev.tag_ids.includes(tagId)
        ? prev.tag_ids.filter(id => id !== tagId)
        : [...prev.tag_ids, tagId]
    }));
  };

  if (loading) {
    return <div data-testid="loading-state">Loading...</div>;
  }

  return (
    <div data-testid="review-form-page">
      <h1 className="font-serif text-4xl font-bold mb-8">
        {isEdit ? 'Edit Review' : 'Create Review'}
      </h1>

      <form onSubmit={handleSubmit} className="max-w-4xl">
        <div className="space-y-6">
          {/* Title */}
          <div>
            <Label htmlFor="title">Title *</Label>
            <Input
              id="title"
              data-testid="review-title-input"
              value={formData.title}
              onChange={(e) => {
                const title = e.target.value;
                setFormData({
                  ...formData,
                  title,
                  slug: generateSlug(title)
                });
              }}
              required
            />
          </div>

          {/* Slug */}
          <div>
            <Label htmlFor="slug">Slug *</Label>
            <Input
              id="slug"
              data-testid="review-slug-input"
              value={formData.slug}
              onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
              required
            />
          </div>

          {/* Product Name */}
          <div>
            <Label htmlFor="product_name">Product Name *</Label>
            <Input
              id="product_name"
              data-testid="review-product-input"
              value={formData.product_name}
              onChange={(e) => setFormData({ ...formData, product_name: e.target.value })}
              required
            />
          </div>

          {/* Description */}
          <div>
            <Label htmlFor="description">Description *</Label>
            <Textarea
              id="description"
              data-testid="review-description-input"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={5}
              required
            />
          </div>

          {/* Category & Rating */}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <Label htmlFor="category">Category *</Label>
              <Select
                value={formData.category_id.toString()}
                onValueChange={(value) => setFormData({ ...formData, category_id: parseInt(value) })}
              >
                <SelectTrigger data-testid="review-category-select">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id.toString()}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="rating">Rating (1-5) *</Label>
              <Input
                id="rating"
                type="number"
                min="1"
                max="5"
                step="0.5"
                data-testid="review-rating-input"
                value={formData.rating}
                onChange={(e) => setFormData({ ...formData, rating: e.target.value })}
                required
              />
            </div>
          </div>

          {/* Pros */}
          <div>
            <Label htmlFor="pros">Pros *</Label>
            <Textarea
              id="pros"
              data-testid="review-pros-input"
              value={formData.pros}
              onChange={(e) => setFormData({ ...formData, pros: e.target.value })}
              rows={4}
              placeholder="One pro per line"
              required
            />
          </div>

          {/* Cons */}
          <div>
            <Label htmlFor="cons">Cons *</Label>
            <Textarea
              id="cons"
              data-testid="review-cons-input"
              value={formData.cons}
              onChange={(e) => setFormData({ ...formData, cons: e.target.value })}
              rows={4}
              placeholder="One con per line"
              required
            />
          </div>

          {/* Specs */}
          <div>
            <Label htmlFor="specs">Specifications *</Label>
            <Textarea
              id="specs"
              data-testid="review-specs-input"
              value={formData.specs}
              onChange={(e) => setFormData({ ...formData, specs: e.target.value })}
              rows={4}
              placeholder="Product specifications"
              required
            />
          </div>

          {/* Featured Image URL */}
          <div>
            <Label htmlFor="featured_image">Featured Image URL *</Label>
            <Input
              id="featured_image"
              data-testid="review-image-input"
              value={formData.featured_image}
              onChange={(e) => setFormData({ ...formData, featured_image: e.target.value })}
              placeholder="https://example.com/image.jpg"
              required
            />
          </div>

          {/* Affiliate Link */}
          <div>
            <Label htmlFor="affiliate_link">Affiliate Link (optional)</Label>
            <Input
              id="affiliate_link"
              data-testid="review-affiliate-input"
              value={formData.affiliate_link}
              onChange={(e) => setFormData({ ...formData, affiliate_link: e.target.value })}
              placeholder="https://example.com/buy"
            />
          </div>

          {/* Author */}
          <div>
            <Label htmlFor="author">Author *</Label>
            <Input
              id="author"
              data-testid="review-author-input"
              value={formData.author}
              onChange={(e) => setFormData({ ...formData, author: e.target.value })}
              required
            />
          </div>

          {/* Tags */}
          <div>
            <Label>Tags</Label>
            <div className="flex flex-wrap gap-3 mt-2">
              {tags.map(tag => (
                <div key={tag.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={`tag-${tag.id}`}
                    checked={formData.tag_ids.includes(tag.id)}
                    onCheckedChange={() => toggleTag(tag.id)}
                  />
                  <label htmlFor={`tag-${tag.id}`} className="text-sm cursor-pointer">
                    {tag.name}
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* Published */}
          <div className="flex items-center space-x-2">
            <Checkbox
              id="published"
              data-testid="review-published-checkbox"
              checked={formData.published}
              onCheckedChange={(checked) => setFormData({ ...formData, published: checked })}
            />
            <Label htmlFor="published" className="cursor-pointer">
              Publish immediately
            </Label>
          </div>

          {error && (
            <div data-testid="form-error" className="p-4 bg-red-50 border border-red-200 text-red-600 text-sm rounded-sm">
              {error}
            </div>
          )}

          {/* Submit Button */}
          <div className="flex space-x-4">
            <Button
              type="submit"
              data-testid="review-submit-btn"
              disabled={submitting}
              className="px-8"
            >
              {submitting ? 'Saving...' : isEdit ? 'Update Review' : 'Create Review'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/admin/reviews')}
            >
              Cancel
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ReviewForm;